import type { ProcessCellForExportParams, ProcessRowGroupForExportParams } from "ag-grid-enterprise";

// ─── Shared export formatting utilities ────────────────────────────────────
// Used by both AgGridComponent (client-side) and AgGridSsrmComponent (SSRM)
// for CSV/Excel cell value formatting during export.

/**
 * Formats a cell value for export.
 * - Group rows: appends the child count in parentheses.
 * - Array values: joins with a space separator.
 * - All other values: returned as-is.
 */
export function formatCellValue(params: ProcessCellForExportParams): string {
  const { value, node } = params;
  // Skip Tree Data group rows (real `data`) — only classic grouping's synthetic rows need the suffix.
  if (node?.group && !node.data) {
    if (value == null || value === "") return "";
    const count = node.allChildrenCount ?? node.childrenAfterFilter?.length ?? 0;
    return `${value} (${count})`;
  }
  if (Array.isArray(value)) {
    return (value as unknown[]).join(" ");
  }
  return value as string;
}

/**
 * Formats the group/tree column's value for export via `processRowGroupCallback`.
 *
 * Without this, AG Grid's own default for that column ignores `processCellCallback`
 * entirely and instead concatenates every ancestor's value with " -> " prefixes
 * (e.g. "-> Acme -> Acme Sub Org") — this restores a plain value instead.
 *
 * Tree Data group rows carry real row data (unlike classic row grouping's synthetic
 * aggregate rows, where `node.data` is undefined), so when it's present we read the
 * column's field directly off it. Otherwise, fall back to the classic-grouping shape
 * (`formatCellValue`'s group-row branch): the node's key with a child count suffix.
 */
export function formatGroupCellValue(params: ProcessRowGroupForExportParams): string {
  const { node, column } = params;
  const field = column?.getColDef().field;
  const data = node.data as Record<string, unknown> | undefined;

  if (data && field) {
    const value = data[field];
    return value == null ? "" : String(value);
  }

  const label = node.key ?? "";
  if (!label) return "";
  const count = node.allChildrenCount ?? node.childrenAfterFilter?.length ?? 0;
  return `${label} (${count})`;
}

/**
 * The Voter Status grid's `status_label` field is only the filter/group bucket
 * array (e.g. `['Key Surfaced', 'Not Voted']`) — a different concept from the
 * full multi-piece status text the cell actually renders. `status_plain` (built
 * server-side via PlainTextStatusPresenter — no dates, unlike `status`, which is
 * HTML-stripped but still carries the on-screen timestamp captions) always
 * carries that full text on the same row — prefer it for export. Returns
 * `undefined` for any other field so callers fall back to their normal value
 * resolution.
 */
export function resolveStatusLabelExportValue(
  field: string | null | undefined,
  data: Record<string, unknown> | undefined
): string | undefined {
  if (field !== "status_label" || !data) return undefined;
  const status = data["status_plain"];
  return typeof status === "string" ? status : undefined;
}
